/**
 * Universal Admin Search — sticky top bar + results overlay.
 *
 * Mounted at the top of the admin dashboard. Debounced (250 ms) query to
 * `/api/admin/search`, mobile-friendly full-screen overlay on small
 * viewports, side panel on desktop. Clicking a result deep-links into the
 * appropriate tab (Registrations / Inquiries / Tutor Applications /
 * Invoices / App Leads) via the parent's `onNavigate` callback.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Search, X, Loader2, User, Phone, Mail, MapPin, GraduationCap,
    FileText, Briefcase, MessageSquare, Smartphone, CircleUser,
} from "lucide-react";
import { api } from "../../lib/api";

const KIND_META = {
    registration: { label: "Registration", cls: "bg-blue-100 text-blue-700", Icon: User },
    inquiry: { label: "Inquiry", cls: "bg-amber-100 text-amber-700", Icon: MessageSquare },
    tutor: { label: "Tutor", cls: "bg-purple-100 text-purple-700", Icon: Briefcase },
    invoice: { label: "Invoice", cls: "bg-emerald-100 text-emerald-700", Icon: FileText },
    app_lead: { label: "App Lead", cls: "bg-slate-100 text-slate-700", Icon: Smartphone },
};

export default function UniversalSearch({ onNavigate }) {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState({});
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);

    // Debounced fetch
    useEffect(() => {
        const term = q.trim();
        if (term.length < 2) {
            setItems([]); setGroups({}); setTotal(0); setError(null); return;
        }
        const t = setTimeout(async () => {
            setLoading(true); setError(null);
            try {
                const { data } = await api.get(`/admin/search?q=${encodeURIComponent(term)}`);
                setItems(data.items || []);
                setGroups(data.groups || {});
                setTotal(data.total || 0);
            } catch (e) {
                setError("Search unavailable — try again");
                setItems([]); setGroups({}); setTotal(0);
            } finally { setLoading(false); }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    // Close on outside click (desktop popover)
    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Keyboard shortcut ⌘K / Ctrl+K
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                inputRef.current?.focus();
                setOpen(true);
            }
            if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const reset = () => { setQ(""); setItems([]); setGroups({}); setTotal(0); setError(null); inputRef.current?.focus(); };
    const goTo = (item) => {
        if (!item?.deep_link_tab) return;
        onNavigate?.(item.deep_link_tab, item);
        setOpen(false);
    };

    const showOverlay = open && q.trim().length >= 2;

    return (
        <div className="relative flex-1 min-w-0 max-w-2xl xl:max-w-3xl 2xl:max-w-4xl" ref={wrapRef} data-testid="universal-search-wrap">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder="Search parents, students, phone, email, city, board, invoice…"
                    data-testid="universal-search-input"
                    className="w-full pl-9 pr-16 py-2.5 sm:py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/10 focus:outline-none"
                />
                {q && (
                    <button onClick={reset} data-testid="universal-search-clear"
                        className="absolute right-11 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1">
                        <X className="w-4 h-4" />
                    </button>
                )}
                <kbd className="hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">⌘K</kbd>
            </div>

            {showOverlay && (
                <div
                    data-testid="universal-search-overlay"
                    className="fixed sm:absolute left-0 right-0 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-2 sm:w-[min(38rem,90vw)] bg-white border border-slate-200 rounded-none sm:rounded-xl shadow-xl z-50 max-h-[80vh] sm:max-h-[70vh] overflow-hidden flex flex-col"
                >
                    <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between text-xs">
                        <span className="text-slate-500">
                            {loading ? (
                                <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</span>
                            ) : (
                                <>Showing <strong>{total}</strong> result{total === 1 ? "" : "s"} for &ldquo;{q.trim()}&rdquo;</>
                            )}
                        </span>
                        <button onClick={() => setOpen(false)} data-testid="universal-search-close" className="text-slate-400 hover:text-slate-700 sm:hidden">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1">
                        {error ? (
                            <EmptyState msg={error} />
                        ) : (!loading && total === 0) ? (
                            <EmptyState msg={`No records found for "${q.trim()}". Try a different name, phone number, email or city.`} />
                        ) : (
                            Object.entries(groups).map(([key, group]) => (
                                group.count > 0 && (
                                    <div key={key} data-testid={`universal-search-group-${key}`}>
                                        <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                                            {KIND_LABELS[key] || key} · {group.count}
                                        </div>
                                        {group.items.map((it) => (
                                            <ResultRow key={`${it.kind}-${it.id}`} it={it} onOpen={() => goTo(it)} />
                                        ))}
                                    </div>
                                )
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

const KIND_LABELS = {
    registrations: "Demo Registrations",
    inquiries: "Contact Inquiries",
    tutor_applications: "Tutor Applications",
    invoices: "Invoices",
    app_leads: "App Leads",
};

function ResultRow({ it, onOpen }) {
    const meta = KIND_META[it.kind] || KIND_META.registration;
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`universal-search-result-${it.kind}-${it.id}`}
            className="w-full text-left px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 flex items-start gap-3"
        >
            <div className={`shrink-0 w-8 h-8 rounded-lg ${meta.cls} flex items-center justify-center`}>
                <meta.Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[#0F172A] truncate">{it.title || "—"}</span>
                    {it.status && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                            {String(it.status).replace(/_/g, " ")}
                        </span>
                    )}
                    <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                </div>
                {it.subtitle && (
                    <div className="text-xs text-slate-500 truncate">{it.subtitle}</div>
                )}
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                    {it.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{it.phone}</span>}
                    {it.email && <span className="inline-flex items-center gap-1 truncate max-w-[50%]"><Mail className="w-3 h-3" />{it.email}</span>}
                    {(it.city || it.state || it.country) && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[it.city, it.state, it.country].filter(Boolean).join(", ")}
                        </span>
                    )}
                    {(it.grade || it.board) && (
                        <span className="inline-flex items-center gap-1">
                            <GraduationCap className="w-3 h-3" />
                            {[it.grade, it.board].filter(Boolean).join(" · ")}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

function EmptyState({ msg }) {
    return (
        <div className="px-6 py-10 text-center" data-testid="universal-search-empty">
            <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <CircleUser className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-sm text-slate-500">{msg}</p>
        </div>
    );
}
